/**
 * HumanoidCharacter - Procedural humanoid character with animations
 * 
 * Creates a recognizable humanoid figure using Three.js primitives.
 * Separate from physics - this is purely visual and follows the physics body.
 * 
 * Features:
 * - Human proportions (head, torso, arms, legs)
 * - Idle breathing animation
 * - Walking leg/arm swing
 * - Running animation (faster swing)
 * - Smooth rotation toward movement direction
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Character colors - Norse/Viking inspired
const COLORS = {
  skin: '#E8BEAC',
  hair: '#4A3728',
  tunic: '#5D4037',
  tunicAccent: '#8D6E63',
  pants: '#3E2723',
  boots: '#2D1F14',
  belt: '#1A1A1A',
  metal: '#7B7B7B',
};

// Animation parameters
const WALK_SPEED = 5;
const RUN_SPEED = 8;
const IDLE_BOB_SPEED = 2;
const IDLE_BOB_AMOUNT = 0.02;

// Seated pose parameters
const SEATED_HIP_Y_OFFSET = -0.35;  // Lower hips when seated
const SEATED_UPPER_LEG_ROTATION = Math.PI / 2.2; // ~82 degrees - nearly horizontal
const SEATED_LOWER_LEG_ROTATION = Math.PI / 2.5; // ~72 degrees - bent down
const SEATED_ARM_ROTATION = -0.2; // Slight arm rest position

export default function HumanoidCharacter({ 
  isMoving = false, 
  isRunning = false, 
  isSeated = false,  // NEW: seated state
  movementDirection = { x: 0, z: 0 },
  position = [0, 0, 0],
  externalRotation = null, // For remote players - bypass internal rotation calculation
}) {
  const groupRef = useRef();
  const bodyRef = useRef();
  const leftArmRef = useRef();
  const rightArmRef = useRef();
  const leftLegRef = useRef();
  const rightLegRef = useRef();
  const leftLowerLegRef = useRef();
  const rightLowerLegRef = useRef();
  const targetRotation = useRef(0);
  const currentRotation = useRef(0);
  const animTime = useRef(0);
  const seatedTransition = useRef(0); // 0 = standing, 1 = fully seated
  
  // Calculate target rotation from movement direction
  useFrame((state, delta) => {
    if (!groupRef.current) return;
    
    animTime.current += delta;
    
    // If external rotation is provided (remote player), use it directly
    if (externalRotation !== null) {
      // Smooth interpolation for external rotation too
      const rotDiff = externalRotation - currentRotation.current;
      let adjustedDiff = rotDiff;
      if (adjustedDiff > Math.PI) adjustedDiff -= Math.PI * 2;
      if (adjustedDiff < -Math.PI) adjustedDiff += Math.PI * 2;
      currentRotation.current += adjustedDiff * 0.15;
      groupRef.current.rotation.y = currentRotation.current;
    } else {
      // Calculate target rotation from movement direction (local player)
      if (isMoving && (movementDirection.x !== 0 || movementDirection.z !== 0)) {
        targetRotation.current = Math.atan2(movementDirection.x, movementDirection.z);
      }
      
      // Smooth rotation interpolation
      const rotDiff = targetRotation.current - currentRotation.current;
      let adjustedDiff = rotDiff;
      if (adjustedDiff > Math.PI) adjustedDiff -= Math.PI * 2;
      if (adjustedDiff < -Math.PI) adjustedDiff += Math.PI * 2;
      
      currentRotation.current += adjustedDiff * 0.15;
      groupRef.current.rotation.y = currentRotation.current;
    }
    
    // Smoothly transition between standing and seated poses
    const targetSeated = isSeated ? 1 : 0;
    seatedTransition.current += (targetSeated - seatedTransition.current) * 0.1;
    const t = seatedTransition.current;
    
    // Animation based on state
    if (t > 0.1) {
      // SEATED POSE - character is sitting
      // Lower the body
      if (bodyRef.current) {
        bodyRef.current.position.y = SEATED_HIP_Y_OFFSET * t;
      }
      
      // Rotate upper legs forward (sitting)
      if (leftLegRef.current) {
        leftLegRef.current.rotation.x = SEATED_UPPER_LEG_ROTATION * t;
      }
      if (rightLegRef.current) {
        rightLegRef.current.rotation.x = SEATED_UPPER_LEG_ROTATION * t;
      }
      
      // Bend lower legs down
      if (leftLowerLegRef.current) {
        leftLowerLegRef.current.rotation.x = SEATED_LOWER_LEG_ROTATION * t;
      }
      if (rightLowerLegRef.current) {
        rightLowerLegRef.current.rotation.x = SEATED_LOWER_LEG_ROTATION * t;
      }
      
      // Arms in resting position
      if (leftArmRef.current) {
        leftArmRef.current.rotation.x = SEATED_ARM_ROTATION * t;
      }
      if (rightArmRef.current) {
        rightArmRef.current.rotation.x = SEATED_ARM_ROTATION * t;
      }
    } else {
      // STANDING ANIMATION
      const animSpeed = isRunning ? RUN_SPEED : WALK_SPEED;
      const swingAmount = isRunning ? 0.8 : 0.5;
      
      if (isMoving) {
        // Walking/running animation
        const swing = Math.sin(animTime.current * animSpeed) * swingAmount;
        
        // Leg swing
        if (leftLegRef.current) leftLegRef.current.rotation.x = swing;
        if (rightLegRef.current) rightLegRef.current.rotation.x = -swing;
        
        // Reset lower legs for walking
        if (leftLowerLegRef.current) leftLowerLegRef.current.rotation.x *= 0.9;
        if (rightLowerLegRef.current) rightLowerLegRef.current.rotation.x *= 0.9;
        
        // Arm swing (opposite to legs)
        if (leftArmRef.current) leftArmRef.current.rotation.x = -swing * 0.7;
        if (rightArmRef.current) rightArmRef.current.rotation.x = swing * 0.7;
        
        // Slight body bob
        if (bodyRef.current) {
          bodyRef.current.position.y = Math.abs(Math.sin(animTime.current * animSpeed * 2)) * 0.05;
        }
      } else {
        // Idle breathing animation
        const breathe = Math.sin(animTime.current * IDLE_BOB_SPEED) * IDLE_BOB_AMOUNT;
        
        if (bodyRef.current) {
          bodyRef.current.position.y = breathe;
        }
        
        // Reset limbs to neutral
        if (leftLegRef.current) leftLegRef.current.rotation.x *= 0.9;
        if (rightLegRef.current) rightLegRef.current.rotation.x *= 0.9;
        if (leftLowerLegRef.current) leftLowerLegRef.current.rotation.x *= 0.9;
        if (rightLowerLegRef.current) rightLowerLegRef.current.rotation.x *= 0.9;
        if (leftArmRef.current) leftArmRef.current.rotation.x *= 0.9;
        if (rightArmRef.current) rightArmRef.current.rotation.x *= 0.9;
      }
    }
  });
  
  return (
    <group ref={groupRef} position={position}>
      {/* Main body group - for breathing animation */}
      <group ref={bodyRef}>
        {/* Head */}
        <group position={[0, 1.6, 0]}>
          {/* Head base */}
          <mesh castShadow>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
          {/* Hair */}
          <mesh position={[0, 0.08, -0.02]} castShadow>
            <sphereGeometry args={[0.16, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.6]} />
            <meshStandardMaterial color={COLORS.hair} />
          </mesh>
          {/* Beard */}
          <mesh position={[0, -0.08, 0.08]} castShadow>
            <sphereGeometry args={[0.08, 8, 8, 0, Math.PI * 2, Math.PI * 0.3, Math.PI * 0.5]} />
            <meshStandardMaterial color={COLORS.hair} />
          </mesh>
        </group>
        
        {/* Torso */}
        <group position={[0, 1.15, 0]}>
          {/* Upper body */}
          <mesh castShadow>
            <boxGeometry args={[0.4, 0.5, 0.22]} />
            <meshStandardMaterial color={COLORS.tunic} />
          </mesh>
          {/* Shoulders */}
          <mesh position={[0, 0.2, 0]} castShadow>
            <boxGeometry args={[0.5, 0.12, 0.24]} />
            <meshStandardMaterial color={COLORS.tunic} />
          </mesh>
          {/* Belt */}
          <mesh position={[0, -0.22, 0]} castShadow>
            <boxGeometry args={[0.42, 0.08, 0.24]} />
            <meshStandardMaterial color={COLORS.belt} />
          </mesh>
          {/* Belt buckle */}
          <mesh position={[0, -0.22, 0.13]} castShadow>
            <boxGeometry args={[0.08, 0.06, 0.02]} />
            <meshStandardMaterial color={COLORS.metal} metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
        
        {/* Left Arm */}
        <group ref={leftArmRef} position={[-0.3, 1.25, 0]}>
          {/* Upper arm */}
          <mesh position={[0, -0.15, 0]} castShadow>
            <boxGeometry args={[0.12, 0.28, 0.12]} />
            <meshStandardMaterial color={COLORS.tunic} />
          </mesh>
          {/* Lower arm */}
          <mesh position={[0, -0.38, 0]} castShadow>
            <boxGeometry args={[0.1, 0.22, 0.1]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
          {/* Hand */}
          <mesh position={[0, -0.52, 0]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
        </group>
        
        {/* Right Arm */}
        <group ref={rightArmRef} position={[0.3, 1.25, 0]}>
          {/* Upper arm */}
          <mesh position={[0, -0.15, 0]} castShadow>
            <boxGeometry args={[0.12, 0.28, 0.12]} />
            <meshStandardMaterial color={COLORS.tunic} />
          </mesh>
          {/* Lower arm */}
          <mesh position={[0, -0.38, 0]} castShadow>
            <boxGeometry args={[0.1, 0.22, 0.1]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
          {/* Hand */}
          <mesh position={[0, -0.52, 0]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
        </group>
      </group>
      
      {/* Hips/Lower torso - pivot point for legs */}
      <group position={[0, 0.75, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.35, 0.15, 0.2]} />
          <meshStandardMaterial color={COLORS.pants} />
        </mesh>
      </group>
      
      {/* Left Leg - restructured with separate upper/lower for seated pose */}
      <group ref={leftLegRef} position={[-0.1, 0.65, 0]}>
        {/* Upper leg (thigh) */}
        <mesh position={[0, -0.17, 0]} castShadow>
          <boxGeometry args={[0.14, 0.3, 0.14]} />
          <meshStandardMaterial color={COLORS.pants} />
        </mesh>
        {/* Lower leg group - pivots at knee */}
        <group ref={leftLowerLegRef} position={[0, -0.32, 0]}>
          {/* Shin */}
          <mesh position={[0, -0.18, 0]} castShadow>
            <boxGeometry args={[0.12, 0.3, 0.12]} />
            <meshStandardMaterial color={COLORS.pants} />
          </mesh>
          {/* Boot */}
          <mesh position={[0, -0.38, 0.03]} castShadow>
            <boxGeometry args={[0.13, 0.12, 0.2]} />
            <meshStandardMaterial color={COLORS.boots} />
          </mesh>
        </group>
      </group>
      
      {/* Right Leg - restructured with separate upper/lower for seated pose */}
      <group ref={rightLegRef} position={[0.1, 0.65, 0]}>
        {/* Upper leg (thigh) */}
        <mesh position={[0, -0.17, 0]} castShadow>
          <boxGeometry args={[0.14, 0.3, 0.14]} />
          <meshStandardMaterial color={COLORS.pants} />
        </mesh>
        {/* Lower leg group - pivots at knee */}
        <group ref={rightLowerLegRef} position={[0, -0.32, 0]}>
          {/* Shin */}
          <mesh position={[0, -0.18, 0]} castShadow>
            <boxGeometry args={[0.12, 0.3, 0.12]} />
            <meshStandardMaterial color={COLORS.pants} />
          </mesh>
          {/* Boot */}
          <mesh position={[0, -0.38, 0.03]} castShadow>
            <boxGeometry args={[0.13, 0.12, 0.2]} />
            <meshStandardMaterial color={COLORS.boots} />
          </mesh>
        </group>
      </group>
    </group>
  );
}
